/**
 * AGC: Quest Card Component for Origami Battle 2.0 - Version 6.8.5
 * AGC: Simple design matching Profile.tsx - no cards, line dividers, compact
 * AGC: Displays individual quest with verify and claim buttons
 * AGC: Social quests use automatic verification (checks Base App profile)
 * @author AGC (AI-Generated Code) with Cursor
 */

import React, { useState, useRef, useEffect } from 'react'
import { CheckCircle2, Circle, Loader2, Coins } from 'lucide-react'
import { useModal } from '../../hooks/useModal'
import { playSoundSafe } from '../../utils/audio'

interface QuestCardProps {
  quest: {
    id: string
    name: string
    description?: string
    type?: string
    difficulty: 'EASY' | 'MEDIUM' | 'HARD' | 'LEGENDARY'
    requiredCount: number
    rewardOGB: number
    progress?: number
    completed?: boolean
    rewardsClaimed?: boolean
  }
  onVerify?: (questId: string) => Promise<void>
  onClaim?: (questId: string) => Promise<void>
  category: 'DAILY' | 'WEEKLY' | 'MONTHLY'
}

export const QuestCard: React.FC<QuestCardProps> = ({ quest, onVerify, onClaim, category }) => {
  const [verifying, setVerifying] = useState(false)
  const [claiming, setClaiming] = useState(false)
  
  // AGC: Modal hook for Base App compatible alerts
  const { alert, Modals } = useModal()
  
  // AGC: Coin sound interval ref for claim animation
  const coinSoundIntervalRef = useRef<NodeJS.Timeout | null>(null)
  
  // AGC: Cleanup coin sound on unmount
  useEffect(() => {
    return () => {
      if (coinSoundIntervalRef.current) {
        clearInterval(coinSoundIntervalRef.current)
      }
    }
  }, [])
  
  const progress = quest.progress || 0
  const progressPercent = quest.requiredCount > 0 
    ? Math.min((progress / quest.requiredCount) * 100, 100) 
    : 0
  
  // AGC: Difficulty colors with LEGENDARY support
  const difficultyColors = {
    EASY: 'text-emerald-300 bg-emerald-600/20 border-emerald-500/40',
    MEDIUM: 'text-yellow-300 bg-yellow-600/20 border-yellow-500/40',
    HARD: 'text-red-300 bg-red-600/20 border-red-500/40',
    LEGENDARY: 'text-purple-300 bg-purple-600/20 border-purple-500/40'
  }
  
  // AGC: Check if this is a social quest (Base App post/comment)
  const isSocialQuest = quest.type === 'social_post' || quest.type === 'social_comment'

  const handleVerify = async () => {
    if (!onVerify) return
    
    // AGC: Play click sound
    playSoundSafe('/sounds/click.mp3', 0.3)
    
    try {
      setVerifying(true)
      await onVerify(quest.id)
    } catch (error: any) {
      console.error('❌ Error verifying quest:', error)
      await alert(error.message || 'Failed to verify quest', { type: 'error' })
    } finally {
      setVerifying(false)
    }
  }

  const handleClaim = async () => {
    if (!onClaim || quest.rewardsClaimed || !quest.completed) return
    
    // AGC: Play click sound
    playSoundSafe('/sounds/click.mp3', 0.3)
    
    try {
      setClaiming(true)
      
      // AGC: Start playing coin sound repeatedly while claiming
      playSoundSafe('/sounds/coin.wav', 0.5)
      coinSoundIntervalRef.current = setInterval(() => {
        playSoundSafe('/sounds/coin.wav', 0.5)
      }, 800) // Play every 800ms
      
      await onClaim(quest.id)
      
      // AGC: Stop coin sound on success
      if (coinSoundIntervalRef.current) {
        clearInterval(coinSoundIntervalRef.current)
        coinSoundIntervalRef.current = null
      }
      
      // AGC: Play congratulations sound
      playSoundSafe('/sounds/congratulations.mp3', 0.6)
      
    } catch (error: any) {
      console.error('❌ Error claiming quest:', error)
      
      // AGC: Stop coin sound on error
      if (coinSoundIntervalRef.current) {
        clearInterval(coinSoundIntervalRef.current)
        coinSoundIntervalRef.current = null
      }
      
      await alert(error.message || 'Failed to claim quest reward', { type: 'error' })
    } finally {
      setClaiming(false)
    }
  }

  const showVerifyButton = !quest.completed && !quest.rewardsClaimed
  const showClaimButton = quest.completed && !quest.rewardsClaimed

  return (
    <div className="py-3 border-b border-white/10 last:border-b-0">
      {/* AGC: Quest Header Row */}
      <div className="flex items-start justify-between gap-2 mb-2">
        {/* AGC: Left side - Status + Name + Difficulty */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {/* AGC: Status Icon */}
          <div className="flex-shrink-0">
            {quest.rewardsClaimed ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            ) : quest.completed ? (
              <Circle className="w-5 h-5 text-yellow-400 fill-yellow-400" />
            ) : (
              <Circle className="w-5 h-5 text-slate-500" />
            )}
          </div>
          
          {/* AGC: Quest Name */}
          <h3 className="text-sm font-semibold text-white truncate flex-1">{quest.name}</h3>
          
          {/* AGC: Difficulty Badge */}
          <span className={`text-[10px] font-medium px-2 py-0.5 rounded border flex-shrink-0 ${difficultyColors[quest.difficulty]}`}>
            {quest.difficulty}
          </span>
        </div>
        
        {/* AGC: Right side - Action Button */}
        <div className="flex-shrink-0">
          {showVerifyButton && (
            <button
              onClick={handleVerify}
              disabled={verifying}
              className="px-3 py-1.5 text-[11px] font-semibold bg-blue-600/20 border border-blue-400/40 text-blue-300 hover:bg-blue-600/30 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
              title={isSocialQuest ? 'Automatically checks your Base App profile' : 'Verify quest completion'}
            >
              {verifying ? (
                <>
                  <Loader2 className="w-3 h-3 animate-spin" />
                  <span>Verifying...</span>
                </>
              ) : (
                <>
                  <span>Verify</span>
                  {isSocialQuest && <span className="text-[9px] opacity-75">(Auto)</span>}
                </>
              )}
            </button>
          )}
          
          {showClaimButton && (
            <button
              onClick={handleClaim}
              disabled={claiming}
              className="px-3 py-1.5 text-[11px] font-semibold bg-emerald-600/20 border border-emerald-400/40 text-emerald-300 hover:bg-emerald-600/30 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
            >
              {claiming ? (
                <>
                  <Loader2 className="w-3 h-3 animate-spin" />
                  <span>Claiming...</span>
                </>
              ) : (
                <>
                  <Coins className="w-3 h-3" />
                  <span>Claim</span>
                </>
              )}
            </button>
          )}
          
          {quest.rewardsClaimed && (
            <span className="px-3 py-1.5 text-[11px] font-semibold bg-slate-600/20 border border-slate-400/30 text-slate-400 rounded-lg flex items-center gap-1.5">
              <CheckCircle2 className="w-3 h-3" />
              Claimed
            </span>
          )}
        </div>
      </div>

      {/* AGC: Description (optional) */}
      {quest.description && (
        <p className="text-[11px] text-slate-400 mb-2 pl-7">{quest.description}</p>
      )}

      {/* AGC: Progress Bar */}
      <div className="pl-7 mb-2">
        <div className="flex justify-between text-[10px] text-slate-400 mb-1">
          <span>{progress} / {quest.requiredCount}</span>
          <span>{Math.round(progressPercent)}%</span>
        </div>
        <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${
              quest.completed 
                ? 'bg-gradient-to-r from-emerald-500 to-emerald-400' 
                : 'bg-gradient-to-r from-blue-500 to-cyan-400'
            }`}
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* AGC: Reward Badge */}
      <div className="pl-7">
        <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 bg-yellow-600/20 border border-yellow-500/40 rounded text-yellow-300">
          <Coins className="w-3 h-3" />
          +{quest.rewardOGB} OGB
        </span>
      </div>
      
      {/* AGC: Social Quest Info */}
      {isSocialQuest && !quest.completed && (
        <div className="mt-2 pl-7">
          <p className="text-[10px] text-slate-400">
            {quest.type === 'social_post' 
              ? '💡 Post about Origami Battle on Base App with link: origamibattle.app/farcaster/'
              : '💡 Comment on @farmpro posts on Base App today'
            }
          </p>
        </div>
      )}

      {/* AGC: Modal components */}
      {Modals}
    </div>
  )
}
